import React from 'react';

const WhyChooseZap = ({ isEmbedded = false }) => {
    return (
        <div className={`w-full max-w-4xl mx-auto space-y-12 ${isEmbedded ? '' : 'py-12 px-6'}`}>

            {!isEmbedded && (
                <div className="text-center mb-10">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-3">Why Choose Zap?</h1>
                    <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                        Zero-faff receipt capture. Structured records ready for your accountant.
                    </p>
                </div>
            )}

            {/* Section 1: Who is Zap made for? */}
            <div className="bg-slate-900/60 p-6 md:p-8 rounded-3xl border border-white/5 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
                <h2 className="text-xl font-bold text-white mb-4">Who is Zap made for?</h2>
                <p className="text-slate-300 mb-6 font-medium text-lg">Sole traders and individuals who:</p>
                <ul className="space-y-4 text-slate-300 mb-8">
                    <li className="flex gap-3 items-start">
                        <span className="text-red-400 mt-1 shrink-0">✕</span>
                        <span className="leading-relaxed"><strong>Hate wasting time</strong> on recording and claiming expenses.</span>
                    </li>
                    <li className="flex gap-3 items-start">
                        <span className="text-red-400 mt-1 shrink-0">✕</span>
                        <span className="leading-relaxed">Had enough of the <strong>laggy, buggy or clunky</strong> mainstream accounting apps.</span>
                    </li>
                    <li className="flex gap-3 items-start">
                        <span className="text-green-400 mt-1 shrink-0">✓</span>
                        <span className="leading-relaxed">Looking for a <strong>lean, effective solution</strong> without the "bells and whistles" (or the price tag) of mainstream accounting software.</span>
                    </li>
                </ul>

                <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-8">
                    <p className="text-white font-medium leading-relaxed">
                        We offer an expense-tracking solution seamless enough to actually eliminate lost receipts. Scan a physical receipt in less than 5 seconds, then throw it away. WhatsApp is the smoothest app experience available; why use anything else?

                    </p>
                </div>

                <div className="text-center md:text-left">
                    <a href="/speed-test" className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 rounded-xl font-bold text-sm hover:bg-indigo-600/40 transition-all shadow-lg shadow-indigo-500/10">
                        <span>⏱️</span> See why Zap is 4x faster than traditional apps →
                    </a>
                </div>
            </div>

            {/* Section 2: Audience Use Cases */}
            <div className="grid md:grid-cols-3 gap-6">
                {[
                    {
                        role: "Landlords",
                        icon: "🏠",
                        text: "Claim 45p/mile for every viewing trip. Just WhatsApp the miles between locations - no spreadsheets, no login, no faff."
                    },
                    {
                        role: "Tradies",
                        icon: "🔨",
                        text: "Snap your material receipts on-site and forward those Screwfix/Travis Perkins email orders. No more lost receipts or shoebox Sundays."
                    },
                    {
                        role: "Busy Sole Traders",
                        icon: "💼",
                        text: "Every receipt you forget to claim eats into your profits. Claim it back in 5 seconds and throw it away!"
                    }
                ].map((useCase, idx) => (
                    <div key={idx} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col items-center md:items-start text-center md:text-left hover:border-indigo-500/50 transition-colors group">
                        <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center text-2xl mb-4 group-hover:bg-indigo-500/20 transition-colors border border-white/5">
                            {useCase.icon}
                        </div>
                        <h3 className="text-white font-bold text-lg mb-3">{useCase.role}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            {useCase.text}
                        </p>
                    </div>
                ))}
            </div>

            {/* Section 3: The Bridging Note */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl relative">
                <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
                <div className="p-6 md:p-8">
                    <h2 className="text-xl font-bold text-white mb-4">How Does Filing Work?</h2>
                    <div className="prose prose-invert max-w-none text-slate-300 mb-8">
                        <p className="text-lg">
                            Wait, do I still need filing software? <strong className="text-white">Yes — and Zap does not replace it.</strong>
                        </p>
                        <p>
                            Zap captures your receipts and prepares structured records. To submit quarterly updates to HMRC, you or your accountant use compatible filing software (such as a bridging tool or full accounting package). Zap's CSV exports are designed for easy import into these tools.
                        </p>
                        <div className="text-[10px] text-slate-500 italic mt-1">
                            Note: 123Sheets is an example of separate filing software, not a Zap integration. See <a href="https://123sheets.com/self-employed.aspx" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-400 transition-colors">123Sheets pricing</a>.
                        </div>
                    </div>

                    <div className="bg-emerald-500/10 border border-emerald-500/20 p-5 rounded-xl">
                        <h4 className="text-emerald-400 font-bold text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            How It Fits Together
                        </h4>
                        <p className="text-emerald-100/80 text-sm leading-relaxed">
                            You capture receipts → Zap extracts and structures the data → you or your accountant review → export CSV into compatible software → compatible software submits to HMRC. Zap handles capture and preparation; submission is separate.
                        </p>
                        <div className="mt-3 text-[10px] text-emerald-500/60 italic border-t border-emerald-500/10 pt-2">
                            Source: <a href="https://www.gov.uk/guidance/use-making-tax-digital-for-income-tax/create-digital-records" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">HMRC: Create digital records (MTD for IT)</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 4: Comparison Table */}
            <div className="bg-slate-900/40 rounded-3xl border border-white/5 p-6 md:p-8 overflow-x-auto shadow-2xl">
                <h2 className="text-xl font-bold text-white mb-6 text-center">Software Approach Comparison</h2>
                <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                        <tr>
                            <th className="p-4 border-b border-slate-700 bg-slate-900/80 font-bold text-slate-300 w-1/4 rounded-tl-xl text-sm md:text-base">Feature</th>
                            <th className="p-4 border-b border-slate-700 bg-slate-900/80 font-bold text-slate-300 w-[37.5%] text-sm md:text-base">Competitor (Avg)</th>
                            <th className="p-4 border-b border-indigo-500/30 bg-indigo-900/20 font-bold text-indigo-300 w-[37.5%] rounded-tr-xl text-sm md:text-base">Zap Expenses + Bridging</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm md:text-sm text-slate-300 divide-y divide-slate-800">
                        <tr className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4 font-semibold text-white">Total Monthly Cost</td>
                            <td className="p-4 text-slate-400">£10.00 (bare bones; VAT unregistered) – £43.00</td>
                            <td className="p-4 bg-indigo-500/5 font-semibold text-indigo-200">£6.95/£8.95 <span className="block text-xs font-normal text-indigo-400/80">£4.95/£6.95 Zap + £2 bridging</span></td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4 font-semibold text-white">User Experience</td>
                            <td className="p-4 text-slate-400"><strong className="text-slate-300 block mb-1">"App-Heavy":</strong> Log in, snap, categorize, sync. Another app on your home screen.</td>
                            <td className="p-4 bg-indigo-500/5 text-indigo-200"><strong className="text-indigo-300 block mb-1">"Zero-Faff":</strong> Send a WhatsApp photo. Done. Just a contact in your WhatsApp.</td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4 font-semibold text-white">Volume Limits</td>
                            <td className="p-4 text-slate-400"><strong className="text-slate-300 block mb-1">Forced Upgrades:</strong> Xero Ignite limits you to 10 bills; Sage limits AI capture to 30/mo.</td>
                            <td className="p-4 bg-indigo-500/5 text-indigo-200"><strong className="text-emerald-400 block mb-1">Unlimited:</strong> Zap doesn't charge you for being busy. Snap 50 or 150; the price stays the same.</td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4 font-semibold text-white">MTD Filing</td>
                            <td className="p-4 text-slate-400"><strong className="text-slate-300 block mb-1">Integrated:</strong> Submission process inside the app.</td>
                            <td className="p-4 bg-indigo-500/5 text-indigo-200"><strong className="text-amber-400/90 block mb-1">Separate Step:</strong> You (or your accountant) import Zap's structured records into compatible filing software, which submits to HMRC.</td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4 font-semibold text-emerald-400/80">Pros</td>
                            <td className="p-4 text-slate-400">Comprehensive reports, payroll, and stock management.</td>
                            <td className="p-4 bg-indigo-500/5 text-indigo-200">Cheapest capture route; no new apps for clients; accountant-ready structured data.</td>
                        </tr>
                        <tr className="border-b-0 hover:bg-slate-800/30 transition-colors">
                            <td className="p-4 font-semibold rounded-bl-xl text-red-400/80">Cons</td>
                            <td className="p-4 text-slate-400">Expensive; complex UI; high "time-tax" for manual snapping.</td>
                            <td className="p-4 bg-indigo-500/5 rounded-br-xl text-indigo-200">No built-in payroll; requires an Excel bridge to file.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default WhyChooseZap;
